import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from 'antd';
import { useRef } from 'react';
import { useForm, type Control, type FieldPath } from 'react-hook-form';

import { useConfirm } from '@/components/feedback/dialog';
import { FormModal } from '@/components/feedback/modal';
import { CropYearField, FormField } from '@/components/forms';
import { ApiError } from '@/lib/api/errors';

import { useCreateField, useUpdateField } from '../hooks';
import {
  FIELD_DETAILS_MESSAGES,
  fieldDetailsSchema,
  seedFieldName,
  stripCrop,
  stripFieldName,
  type CropYear,
  type FieldDetailsValues,
  type SaveFieldBody,
} from '../schemas';

import styles from './field-details-modal.module.scss';

const SUBTITLE = 'Assign a name to this boundary for easier tracking.';

/** Not designed (one of the ten undesigned states). Mirrors the shipped "Discard Field Markings?"
 *  dialog — Go Back / Discard, non-destructive treatment (grilling Q7 D1). */
const DISCARD = {
  title: 'Discard Field Details?',
  subtitle: "Any details you've entered will be lost.",
  confirmText: 'Discard',
  cancelText: 'Go Back',
} as const;

/**
 * An input that silently drops what the field does not accept, as it is typed.
 *
 * `<TextField>` cannot do this: it spreads `{...rest}` *before* its own `onChange`, deliberately, so
 * a caller cannot intercept the value. `<PhoneField>` is the repo's precedent for the pattern
 * (`onChange={(e) => field.onChange(strip(e.target.value))}`) but hard-codes its own rule. Two
 * call sites, one local component — no third file, and no widening of a shared one.
 */
function StrippedInput({
  name,
  control,
  label,
  required,
  strip,
}: {
  name: FieldPath<FieldDetailsValues>;
  control: Control<FieldDetailsValues>;
  label: string;
  required?: boolean;
  strip: (raw: string) => string;
}) {
  return (
    <FormField name={name} control={control} label={label} required={required}>
      {({ field, fieldState }) => (
        <Input
          id={name}
          placeholder="Enter"
          status={fieldState.error ? 'error' : undefined}
          value={(field.value as string) ?? ''}
          onChange={(event) => field.onChange(strip(event.target.value))}
          onBlur={field.onBlur}
          ref={field.ref}
        />
      )}
    </FormField>
  );
}

/** Only what the form and the write need. Narrower than `MapField` on purpose: the modal has no
 *  business seeing acreage, devices or geometry it is not saving. */
export type EditedField = {
  id: string;
  name: string;
  crop: string | null;
  cropYear: CropYear | null;
};

/** Plain `Omit` collapses a union's `keyof` to the members' shared keys, which would erase `points`
 *  and `circle` alike. `T extends unknown` forces the conditional to run once per branch instead. */
type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;

export type FieldDetailsModalProps = {
  /** The **committed shape** being named — a polygon of at least three **Point**s, or a circle.
   *  Already gated by `Save Mapping`, and already in wire form. */
  shape: DistributiveOmit<SaveFieldBody, 'name' | 'crop' | 'cropYear'>;
  /** The Field being edited, or `undefined` when creating. Supplies the defaults AND selects the
   *  verb — there is no separate `mode` flag to keep in step with it. */
  field?: EditedField;
  /** The chosen **Imported shape**'s `.dbf` name, when this is an imported session (ticket 09).
   *  Sanitised by {@link seedFieldName} on the way into `defaultValues` — a seed, never a change,
   *  which is what keeps `isDirty` false and Cancel silent over a value nobody typed. Ignored when
   *  `field` is present: an edit's own name always wins. */
  suggestedName?: string | null;
  /** Dismissed without saving — the caller returns to Field Mapping mode with the shape intact. */
  onClose: () => void;
  /** Saved. The caller navigates to the Map and focuses this Field. */
  onSaved: (fieldId: string) => void;
};

/**
 * **Add Field Details** (113601, Figma `598:542366`) — the last step before a Field exists.
 *
 * Three commit levels end here: the committed shape arrives as `points`, this form names it, and the
 * server's record is the third. The modal owns only the *field-level* presentation — the two silent
 * strip rules, the length rule and the duplicate-name 409. Everything else the flow says out loud is
 * said by `<MutationToaster>` from `useCreateField`'s `meta`.
 */
export function FieldDetailsModal({
  shape,
  field,
  suggestedName,
  onClose,
  onSaved,
}: FieldDetailsModalProps) {
  const { confirm } = useConfirm();
  const create = useCreateField();
  // Called unconditionally with `''` when creating — hooks may not be conditional, and an unused
  // mutation costs nothing. `field` is what decides which one runs.
  const update = useUpdateField(field?.id ?? '');
  const form = useForm<FieldDetailsValues>({
    resolver: zodResolver(fieldDetailsSchema),
    // AC 4 — Field Name, Crop and Crop Year pre-filled and amendable. `''` for an absent optional,
    // because an antd input's empty value is a string; `submit` nulls it again on the way out.
    // A Field being edited supplies its own name; otherwise the shapefile's, sanitised. Both go
    // through the SAME `defaultValues` path — no `setValue`, which would mark the form dirty and
    // put the discard confirmation in front of a Cancel the AC does not gate.
    defaultValues: {
      name: field?.name ?? seedFieldName(suggestedName),
      crop: field?.crop ?? '',
      cropYear: field?.cropYear ? [field.cropYear.start, field.cropYear.end] : null,
    },
  });
  // Belt-and-braces against a second Save Field click reaching the server as a second create.
  // antd's <Button loading> guards its own click handler (`preventDefault()` while loading), but
  // never sets the native `disabled` attribute from `loading` alone — Modal/FormModal expose no
  // `primaryDisabled` to force it — and `create.isPending` lags one render behind a same-tick
  // second submit. A ref set synchronously, before anything async, is the one guard that is never
  // late for its own race.
  const submittingRef = useRef(false);
  // Read HERE, in the render body, not inside `requestClose` alone. `formState` is an RHF Proxy
  // that only keeps a field current once something has read it **during render** — and today the
  // only such reader is `<FormModal>`'s own `maskClosable` line. Depending on that would make this
  // gate silently always-clean the day that line is refactored, and the confirmation would simply
  // stop appearing. One local read owns the subscription outright.
  const { isDirty } = form.formState;

  async function submit(values: FieldDetailsValues) {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      const body = {
        ...shape,
        name: values.name,
        // Optional means optional (113601): an untouched input is `''`, and the contract wants null.
        crop: values.crop || null,
        cropYear: values.cropYear ? { start: values.cropYear[0], end: values.cropYear[1] } : null,
      } as SaveFieldBody;
      const { id } = field ? await update.mutateAsync(body) : await create.mutateAsync(body);
      onSaved(id);
    } catch (error) {
      // The 409 is the *only* duplicate-name authority — no client-side pre-empt against the Fields
      // list, which would duplicate a server rule and still lose the race. Every other failure is
      // already announced by <MutationToaster>, and the modal simply stays open with its entries.
      if (error instanceof ApiError && error.status === 409) {
        form.setError('name', { message: FIELD_DETAILS_MESSAGES.duplicateName });
      }
    } finally {
      submittingRef.current = false;
    }
  }

  // 113601 attached the confirmation to Cancel, the × and Esc with no qualifier. 113610 gates it on
  // "Given I have **entered** details", which the imported seed makes load-bearing: a
  // `defaultValues` name is a value the Grower never typed, and offering to discard it would be a
  // question about their own file. `isDirty` is exactly that distinction — it compares against the
  // defaults, so a seeded-and-untouched form is clean and an amended one is not.
  // `maskClosable={false}` still closes the one door that would skip this.
  function requestClose() {
    if (!isDirty) {
      onClose();
      return;
    }
    void confirm(DISCARD).then((discard) => {
      if (!discard) return;
      form.reset();
      onClose();
    });
  }

  return (
    <FormModal
      open
      size="lg"
      title="Add Field Details"
      subtitle={SUBTITLE}
      onClose={requestClose}
      onCancel={requestClose}
      resetOnClose={false}
      maskClosable={false}
      form={form}
      onSubmit={submit}
      submitText="Save Field"
      primaryVariant="neutral"
    >
      <div className={styles.grid}>
        <StrippedInput
          name="name"
          control={form.control}
          label="Field Name"
          required
          strip={stripFieldName}
        />
        <StrippedInput name="crop" control={form.control} label="Crop" strip={stripCrop} />
        <CropYearField name="cropYear" control={form.control} label="Crop Year" />
      </div>
    </FormModal>
  );
}
